import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus,
  FileText,
  Trash2,
  Eye,
  Upload,
  ShoppingCart,
  Calendar,
  X,
  Edit2,
  Send,
  Check,
  Mail,
  MessageCircle,
  AlertCircle,
  DollarSign,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import LoadingScreen from '../components/ui/LoadingScreen';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { supplierService } from '../services/supplierService';
import { projectService } from '../services/projectService';
import { generalWorkCategoryService, GeneralWorkCategory } from '../services/generalWorkCategoryService';
import { localDocumentStore, LocalDocument } from '../services/localDocumentStore';
import { supplierInvoiceService } from '../services/supplierInvoiceService';
import { Supplier, SupplierInvoiceResponse } from '../types';
import { buildWhatsAppUrl } from '../utils/whatsapp';
import { showSuccess, showError, showWarning } from '../components/Alerts';
import { PERMISSIONS } from '../constants/permissions';
import RequirePermission from '../components/RequirePermission';
import DocumentsDialog from '../components/PurchaseOrders/DocumentsDialog';
import { useAuth } from '../context/AuthContext';

// Tipos
interface CostDistribution {
  id: string;
  projectId: string;
  projectName: string;
  workCategoryId: string;
  workCategoryName: string;
  percentage: number;
}

interface GeneralCostDistribution {
  id: string;
  generalWorkCategoryId: string;
  generalWorkCategoryName: string;
  percentage: number;
}

interface PurchaseOrderLine {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  subtotal: number;
  costDistributions?: CostDistribution[];
  generalCostDistributions?: GeneralCostDistribution[];
  // Legacy fields
  costCenter?: string;
  project?: string;
  workCategory?: string;
}

interface PurchaseOrderDocument {
  id: string;
  type: 'factura' | 'remito';
  fileName: string;
  uploadDate: string;
}

interface PurchaseOrder {
  id: string;
  orderNumber: string;
  supplierId: string;
  supplierName: string;
  supplierCuit: string;
  date: string;
  accountingType: 'A' | 'B';
  ivaType: string;
  ivaAmount: number;
  subtotal: number;
  total: number;
  currency: 'ARS' | 'USD';
  costCenter: string; // Centro de costo general (opcional)
  lines: PurchaseOrderLine[];
  documents: PurchaseOrderDocument[];
  status: 'draft' | 'sent' | 'received' | 'cancelled';
  notes: string;
}

const ITEMS_PER_PAGE = 12;

const PurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEditStatus = hasPermission(PERMISSIONS.COMPRAS.OC_EDITAR);
  const canManageDocuments = hasPermission(PERMISSIONS.COMPRAS.FACTURAS_EDITAR) || hasPermission(PERMISSIONS.COMPRAS.OC_EDITAR);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [page, setPage] = useState(1);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [projects, setProjects] = useState<Array<{ id: number; name: string; workCategories: Array<{ id: number; value: string }> }>>([]);
  const [generalWorkCategories, setGeneralWorkCategories] = useState<GeneralWorkCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isDocumentsDialogOpen, setIsDocumentsDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [orderToView, setOrderToView] = useState<PurchaseOrder | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<PurchaseOrder | null>(null);
  const [notifySupplierOnCancel, setNotifySupplierOnCancel] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<PurchaseOrder | null>(null);
  const [orderToChangeStatus, setOrderToChangeStatus] = useState<PurchaseOrder | null>(null);
  const [orderForDocuments, setOrderForDocuments] = useState<PurchaseOrder | null>(null);
  const [sendByEmail, setSendByEmail] = useState(true);
  const [sendByWhatsApp, setSendByWhatsApp] = useState(false);
  // Conteo de documentos por orden { orderId: { FACTURA: n, REMITO: n } }
  const [documentCounts, setDocumentCounts] = useState<Record<string, Record<string, number>>>({});
  // Conteo local de remitos (IndexedDB) por orden
  const [localRemitoCounts, setLocalRemitoCounts] = useState<Record<string, number>>({});
  // Facturas y remitos del modal de detalle
  const [detailInvoices, setDetailInvoices] = useState<SupplierInvoiceResponse[]>([]);
  const [detailRemitos, setDetailRemitos] = useState<LocalDocument[]>([]);
  // Visor de archivos del modal de detalle
  const [detailViewerUrl, setDetailViewerUrl] = useState<string | null>(null);
  const [detailViewerTitle, setDetailViewerTitle] = useState('');
  const [detailViewerMime, setDetailViewerMime] = useState('application/pdf');

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setLoadError(null);
      
      try {
        // Cargar proveedores, órdenes, proyectos y categorías en paralelo
        const [suppliersData, ordersData, projectsData, generalCatsData] = await Promise.all([
          supplierService.getAllSuppliers(),
          purchaseOrderService.getAllPurchaseOrders(),
          projectService.getAllSimple(),
          generalWorkCategoryService.getAllGeneralWorkCategories(),
        ]);

        setSuppliers(suppliersData);
        setProjects(projectsData);
        setGeneralWorkCategories(generalCatsData);
        
        // Mapear datos del backend al formato del componente
        const mappedOrders: PurchaseOrder[] = ordersData.map(order => ({
          id: order.id.toString(),
          orderNumber: order.orderNumber,
          supplierId: order.supplierCuit,
          supplierName: order.supplierName,
          supplierCuit: order.supplierCuit,
          date: order.orderDate,
          accountingType: order.accountingType as 'A' | 'B',
          ivaType: order.accountingType === 'A' ? '21%' : 'No aplica',
          ivaAmount: order.ivaTotal,
          subtotal: order.subtotal,
          total: order.total,
          currency: (order.currency || 'ARS') as 'ARS' | 'USD',
          costCenter: '',
          lines: [], // Se cargarán al ver detalles
          documents: [],
          status: mapBackendStatus(order.status),
          notes: ''
        }));
        
        setPurchaseOrders(mappedOrders);
        setIsLoading(false);

        // Cargar conteos de documentos en paralelo (no bloquea la tabla)
        const countsMap: Record<string, Record<string, number>> = {};
        const countPromises = ordersData.map(async (order) => {
          try {
            const counts = await purchaseOrderService.getDocumentCounts(order.id);
            countsMap[order.id.toString()] = counts;
          } catch {
            // Si falla un conteo individual, lo ignoramos
          }
        });
        await Promise.all(countPromises);
        setDocumentCounts(countsMap);

        // Load local remito counts from IndexedDB (non-blocking)
        localDocumentStore.getRemitoCounts()
          .then(setLocalRemitoCounts)
          .catch(() => {});
      } catch (error) {
        console.error('Error cargando datos:', error);
        setLoadError('Error al cargar las órdenes de compra. Por favor, recargue la página.');
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Mapear estados del backend a estados del frontend
  const mapBackendStatus = (backendStatus: string): 'draft' | 'sent' | 'received' | 'cancelled' => {
    const statusMap: Record<string, 'draft' | 'sent' | 'received' | 'cancelled'> = {
      'BORRADOR': 'draft',
      'ENVIADO': 'sent',
      'RECIBIDO': 'received',
      'CANCELADO': 'cancelled'
    };
    return statusMap[backendStatus] || 'draft';
  };

  const canCancelOrder = (status: string): boolean => {
    return status === 'draft' || status === 'sent';
  };

  const getNextStatus = (currentStatus: string): string | null => {
    const statusFlow = {
      draft: 'sent',
      sent: 'received',
      received: null
    };
    return statusFlow[currentStatus as keyof typeof statusFlow] || null;
  };

  const getStatusLabel = (status: string): string => {
    const labels = {
      draft: 'Enviar al Proveedor',
      sent: 'Marcar como Recibida',
      received: 'Completada'
    };
    return labels[status as keyof typeof labels] || '';
  };

  const handleChangeStatus = (order: PurchaseOrder) => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) {
      showWarning('Estado final', 'Esta orden ya está en su estado final');
      return;
    }

    // Resetear opciones de envío
    setSendByEmail(true);
    setSendByWhatsApp(false);
    setOrderToChangeStatus(order);
    setIsStatusDialogOpen(true);
  };

  const handleCancelOrder = (order: PurchaseOrder) => {
    if (order.status === 'cancelled') {
      showWarning('Orden cancelada', 'Esta orden ya está cancelada');
      return;
    }
    if (order.status === 'received') {
      showWarning('Acción no permitida', 'No se puede cancelar una orden que ya fue recibida');
      return;
    }
    const supplier = suppliers.find(s => s.cuit === order.supplierCuit);
    const hasEmail = !!(supplier?.contactEmail || supplier?.email);
    setNotifySupplierOnCancel(order.status === 'sent' && hasEmail);
    setOrderToCancel(order);
    setIsCancelDialogOpen(true);
  };

  const confirmCancelOrder = async () => {
    if (!orderToCancel) return;

    try {
      await purchaseOrderService.updatePurchaseOrderStatus(Number(orderToCancel.id), 'CANCELADO');

      const updatedOrders = purchaseOrders.map(order =>
        order.id === orderToCancel.id
          ? { ...order, status: 'cancelled' as 'draft' | 'sent' | 'received' | 'cancelled' }
          : order
      );

      setPurchaseOrders(updatedOrders);
      setIsCancelDialogOpen(false);

      if (notifySupplierOnCancel) {
        try {
          await purchaseOrderService.notifyCancellation(Number(orderToCancel.id));
          showSuccess(`Orden ${orderToCancel.orderNumber} cancelada y proveedor notificado por email.`);
        } catch {
          showSuccess(`Orden ${orderToCancel.orderNumber} cancelada.`);
          showError('No se pudo enviar el email de cancelación al proveedor.');
        }
      } else {
        showSuccess(`¡Orden de compra ${orderToCancel.orderNumber} cancelada con éxito!`);
      }

      setOrderToCancel(null);
      setNotifySupplierOnCancel(false);
    } catch (error: any) {
      console.error('Error al cancelar orden:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al cancelar la orden';
      showError('Error al cancelar la orden: ' + errorMessage);
    }
  };

  const handleDeleteOrder = (order: PurchaseOrder) => {
    if (order.status !== 'draft') {
      showWarning('Acción no permitida', 'Solo se pueden borrar órdenes en estado Borrador');
      return;
    }
    setOrderToDelete(order);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    try {
      // Eliminar la orden en el backend
      await purchaseOrderService.deletePurchaseOrder(Number(orderToDelete.id));

      const updatedOrders = purchaseOrders.filter(order => order.id !== orderToDelete.id);

      setPurchaseOrders(updatedOrders);
      setIsDeleteDialogOpen(false);
      setOrderToDelete(null);
      showSuccess(`¡Orden de compra ${orderToDelete.orderNumber} eliminada con éxito!`);
    } catch (error: any) {
      console.error('Error al eliminar orden:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al eliminar la orden';
      showError('Error al eliminar la orden: ' + errorMessage);
    }
  };

  const confirmStatusChange = async () => {
    if (!orderToChangeStatus) return;

    const nextStatus = getNextStatus(orderToChangeStatus.status);
    if (!nextStatus) return;

    // Validar que se haya seleccionado al menos un método de envío cuando es borrador->enviado
    if (orderToChangeStatus.status === 'draft' && !sendByEmail && !sendByWhatsApp) {
      showWarning('Método de envío requerido', 'Debe seleccionar al menos un método de envío');
      return;
    }

    try {
      // Mapear estado del frontend al backend
      const backendStatus = nextStatus === 'sent' ? 'ENVIADO' : nextStatus === 'received' ? 'RECIBIDO' : 'BORRADOR';
      
      // Actualizar estado en el backend
      await purchaseOrderService.updatePurchaseOrderStatus(Number(orderToChangeStatus.id), backendStatus);

      // Email: el backend lo envía automáticamente al cambiar a ENVIADO si el proveedor tiene email.
      // WhatsApp: abrir wa.me con mensaje pre-compuesto
      if (orderToChangeStatus.status === 'draft' && sendByWhatsApp) {
        const supplier = suppliers.find(s => s.cuit === orderToChangeStatus.supplierCuit);
        const phone = supplier?.contactPhone || supplier?.phone || null;
        
        try {
          // Fetch full order detail to include items in message
          const detail = await purchaseOrderService.getPurchaseOrderById(Number(orderToChangeStatus.id));
          const currSymbol = orderToChangeStatus.currency === 'USD' ? 'USD' : '$';
          const fmtNum = (n: number) => n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

          let msg = `*ORDEN DE COMPRA ${orderToChangeStatus.orderNumber}*\n`;
          msg += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;
          msg += `📅 *Fecha:* ${orderToChangeStatus.date}\n`;
          msg += `🏢 *Proveedor:* ${orderToChangeStatus.supplierName}\n`;
          msg += `📋 *CUIT:* ${orderToChangeStatus.supplierCuit}\n`;
          msg += `💰 *Moneda:* ${orderToChangeStatus.currency}\n`;
          msg += `📄 *Tipo Factura:* ${orderToChangeStatus.accountingType}\n\n`;

          msg += `*DETALLE DE LA ORDEN*\n`;
          msg += `━━━━━━━━━━━━━━━━━━━━━━\n`;
          if (detail.items && detail.items.length > 0) {
            detail.items.forEach((item, idx) => {
              msg += `\n${idx + 1}. *${item.description}*\n`;
              msg += `   Cant: ${item.quantity} ${item.unit} | P.Unit: ${currSymbol} ${fmtNum(item.unitPrice)} | Subtotal: ${currSymbol} ${fmtNum(item.lineSubtotal)}\n`;
            });
          }

          msg += `\n━━━━━━━━━━━━━━━━━━━━━━\n`;
          msg += `   Subtotal: ${currSymbol} ${fmtNum(orderToChangeStatus.subtotal)}\n`;
          if (orderToChangeStatus.accountingType === 'A' && orderToChangeStatus.ivaAmount > 0) {
            msg += `   IVA: ${currSymbol} ${fmtNum(orderToChangeStatus.ivaAmount)}\n`;
          }
          msg += `   *TOTAL: ${currSymbol} ${fmtNum(orderToChangeStatus.total)}*\n`;
          msg += `━━━━━━━━━━━━━━━━━━━━━━\n`;

          if (detail.notes && detail.notes.trim()) {
            msg += `\n📝 *Notas:* ${detail.notes.trim()}\n`;
          }

          msg += `\nPor favor, confirme la recepción de esta orden. ¡Gracias!`;

          const url = buildWhatsAppUrl(phone, msg);
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        } catch (e) {
          // Fallback to basic message if detail fetch fails
          console.error('Error fetching order detail for WhatsApp:', e);
          const basicMsg =
            `Hola, le informamos que hemos emitido la Orden de Compra ${orderToChangeStatus.orderNumber} ` +
            `por un total de ${orderToChangeStatus.currency} ${orderToChangeStatus.total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}. ` +
            `Por favor confirme la recepción. Gracias.`;
          const url = buildWhatsAppUrl(phone, basicMsg);
          if (url) {
            window.open(url, '_blank', 'noopener,noreferrer');
          }
        }
      }

      const updatedOrders = purchaseOrders.map(order => 
        order.id === orderToChangeStatus.id 
          ? { ...order, status: nextStatus as 'draft' | 'sent' | 'received' }
          : order
      );

      setPurchaseOrders(updatedOrders);
      setIsStatusDialogOpen(false);
      setOrderToChangeStatus(null);
      showSuccess(`¡Estado de la orden ${orderToChangeStatus.orderNumber} actualizado con éxito a ${
        nextStatus === 'sent' ? 'Enviado' : nextStatus === 'received' ? 'Recibido' : nextStatus
      }!`);
    } catch (error: any) {
      console.error('Error al actualizar estado:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al actualizar el estado';
      showError('Error al cambiar estado: ' + errorMessage);
    }
  };

  const handleViewDetails = async (order: PurchaseOrder) => {
    setIsViewDialogOpen(true);
    setIsLoadingDetails(true);
    setDetailInvoices([]);
    setDetailRemitos([]);
    
    try {
      // Cargar detalles completos, facturas y remitos en paralelo
      const [details, invoices, localDocs] = await Promise.all([
        purchaseOrderService.getPurchaseOrderById(Number(order.id)),
        supplierInvoiceService.getInvoicesByPurchaseOrder(Number(order.id)).catch(() => [] as SupplierInvoiceResponse[]),
        localDocumentStore.getByOrder(order.id).catch(() => [] as LocalDocument[]),
      ]);
      
      // Mapear los detalles al formato del componente
      const detailedOrder: PurchaseOrder = {
        ...order,
        currency: (details.currency || 'ARS') as 'ARS' | 'USD', // Obtener moneda del backend
        subtotal: details.subtotal,
        total: details.total,
        lines: details.items.map(item => ({
          id: item.id.toString(),
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          subtotal: item.lineSubtotal,
          costDistributions: item.allocations
            .filter(alloc => alloc.costCenterType === 'PROJECT')
            .map(alloc => {
              const project = projects.find(p => p.id === alloc.projectId);
              const workCategory = project?.workCategories.find(wc => wc.id === alloc.workCategoryId);
              return {
                id: `${alloc.projectId}-${alloc.workCategoryId}`,
                projectId: alloc.projectId?.toString() || '',
                projectName: project?.name || `Proyecto #${alloc.projectId}`,
                workCategoryId: alloc.workCategoryId.toString(),
                workCategoryName: workCategory?.value || `Rubro #${alloc.workCategoryId}`,
                percentage: item.quantity ? (alloc.allocatedQuantity / item.quantity) * 100 : 0,
              };
            }),
          generalCostDistributions: item.allocations
            .filter(alloc => alloc.costCenterType === 'GENERAL')
            .map(alloc => {
              const category = generalWorkCategories.find(c => c.id === alloc.workCategoryId);
              return {
                id: `${alloc.workCategoryId}`,
                generalWorkCategoryId: alloc.workCategoryId.toString(),
                generalWorkCategoryName: category?.name || `Categoría #${alloc.workCategoryId}`,
                percentage: item.quantity ? (alloc.allocatedQuantity / item.quantity) * 100 : 0,
              };
            }),
          costCenter: item.allocations.length === 0 ? order.costCenter : undefined
        })),
        notes: details.notes || '',
        ivaType: details.accountingType === 'A' ? `${details.ivaCode}%` : 'No aplica',
        ivaAmount: details.ivaTotal
      };
      
      setOrderToView(detailedOrder);
      setDetailInvoices(invoices);
      setDetailRemitos(localDocs.filter(d => d.type === 'remito'));
      setIsLoadingDetails(false);
    } catch (error) {
      console.error('Error al cargar detalles de la orden:', error);
      showError('Error al cargar los detalles de la orden. Por favor, inténtelo nuevamente.');
      setIsViewDialogOpen(false);
      setIsLoadingDetails(false);
    }
  };

  // ── Visor de archivos para el modal de detalle ──────────────────────────────

  const openDetailViewer = async (fileUrl: string, fileName: string, mime: string) => {
    let blobUrl: string | null = null;
    if (fileUrl.startsWith('local://')) {
      const docId = fileUrl.replace('local://', '');
      blobUrl = await localDocumentStore.createObjectUrl(docId);
      if (!blobUrl) {
        showError('El archivo no está disponible localmente.');
        return;
      }
    } else if (fileUrl.startsWith('http')) {
      blobUrl = fileUrl;
    } else {
      showError('URL de archivo no reconocida');
      return;
    }
    setDetailViewerTitle(fileName);
    setDetailViewerMime(mime);
    setDetailViewerUrl(blobUrl);
  };

  const openDetailRemitoViewer = async (doc: LocalDocument) => {
    const blob = await localDocumentStore.getBlob(doc.id);
    if (!blob) { showError('Archivo no disponible'); return; }
    const url = URL.createObjectURL(blob);
    setDetailViewerTitle(doc.fileName);
    setDetailViewerMime(doc.mimeType);
    setDetailViewerUrl(url);
  };

  const closeDetailViewer = () => {
    if (detailViewerUrl && detailViewerUrl.startsWith('blob:')) URL.revokeObjectURL(detailViewerUrl);
    setDetailViewerUrl(null);
    setDetailViewerTitle('');
    setDetailViewerMime('application/pdf');
  };

  const handleManageDocuments = (order: PurchaseOrder) => {
    setOrderForDocuments(order);
    setIsDocumentsDialogOpen(true);
  };

  const handleInvoiceCreated = async (orderId: string) => {
    try {
      const counts = await purchaseOrderService.getDocumentCounts(Number(orderId));
      setDocumentCounts((prev) => ({ ...prev, [orderId]: counts }));
    } catch { /* non-critical */ }
  };

  const handleRemitoCountChange = (orderId: string, count: number) => {
    setLocalRemitoCounts((prev) => ({ ...prev, [orderId]: count }));
  };

  const getStatusBadge = (status: string, onClick?: () => void) => {
    const statusConfig = {
      draft: { label: 'Borrador', color: 'bg-muted text-muted-foreground hover:bg-muted/80 border-muted-foreground/20' },
      sent: { label: 'Enviado', color: 'bg-primary/10 text-primary hover:bg-primary/20 border-primary/20' },
      received: { label: 'Recibido', color: 'bg-success/10 text-success border-success/20' },
      cancelled: { label: 'Cancelada', color: 'bg-destructive/10 text-destructive border-destructive/20' },
    };
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;

    if (onClick) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className={`px-2.5 py-1 text-xs font-semibold rounded-full border transition-all cursor-pointer hover:shadow-sm active:scale-95 flex items-center gap-1.5 ${config.color}`}
          title="Haga clic para cambiar el estado"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current"></span>
          </span>
          {config.label}
        </button>
      );
    }

    return (
      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${config.color.split(' hover:')[0]}`}>
        {config.label}
      </span>
    );
  };

  const filteredOrders = purchaseOrders.filter(order => {
    const matchesSearch =
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageOrders = filteredOrders.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  // Reset page on filter/search change
  React.useEffect(() => { setPage(1); }, [searchTerm, statusFilter]);

  if (isLoading) return <LoadingScreen message="Cargando órdenes de compra..." />;

  // Mostrar error
  if (loadError) {
    return (
      <div className="w-full h-full p-6">
        <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
          <div className="p-6">
            <div className="flex items-start gap-3 p-4 border rounded-lg bg-destructive/10 border-destructive/20">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-foreground">Error al cargar datos</h3>
                <p className="mt-1 text-sm text-muted-foreground">{loadError}</p>
                <Button onClick={() => window.location.reload()} variant="destructive" size="sm" className="mt-3">
                  Recargar página
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6 space-y-6">
      {/* Header */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        <div className="flex flex-col items-start justify-between gap-4 px-6 py-5 border-b sm:flex-row sm:items-center border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <ShoppingCart className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Órdenes de Compra</h1>
              <p className="text-sm text-muted-foreground">Gestión de compras a proveedores</p>
            </div>
          </div>
          <RequirePermission permission={PERMISSIONS.COMPRAS.OC_CREAR}>
            <Button
              onClick={() => navigate('/purchase-orders/new')}
              size="sm"
              className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              Nueva Orden de Compra
            </Button>
          </RequirePermission>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex flex-col items-end gap-4 md:flex-row">
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase text-muted-foreground">Estado</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px] rounded-lg">
                  <SelectValue placeholder="Todos los estados" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="sent">Enviado</SelectItem>
                  <SelectItem value="received">Recibido</SelectItem>
                  <SelectItem value="cancelled">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">Buscar</label>
              <div className="relative">
                <ShoppingCart className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por número o proveedor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Referencias de Acciones */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Referencias de Acciones</h2>
        </div>
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 flex-shrink-0">
                <Eye className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">Ver</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Ver detalles completos</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-warning/10 flex-shrink-0">
                <Edit2 className="w-3.5 h-3.5 text-warning" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">Editar</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Modificar borrador</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 flex-shrink-0">
                <Send className="w-3.5 h-3.5 text-primary" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">Avanzar</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Cambiar estado</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-success/10 flex-shrink-0">
                <FileText className="w-3.5 h-3.5 text-success" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">Documentos</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Facturas y remitos</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-destructive/10 flex-shrink-0">
                <X className="w-3.5 h-3.5 text-destructive" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">Cancelar</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Inhabilitar orden</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 p-3 rounded-lg border border-border bg-muted/20">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-destructive/10 flex-shrink-0">
                <Trash2 className="w-3.5 h-3.5 text-destructive" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground">Borrar</div>
                <div className="text-[10px] text-muted-foreground leading-tight">Eliminar permanente</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Cards */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        <div className="flex flex-col items-start justify-between gap-2 px-6 py-4 border-b sm:flex-row sm:items-center border-border">
          <h2 className="text-base font-semibold text-foreground">Órdenes Registradas</h2>
          <span className="text-xs text-muted-foreground">
            {filteredOrders.length} orden{filteredOrders.length !== 1 ? 'es' : ''} encontrada{filteredOrders.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="p-6">
          {filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-muted/50">
                <ShoppingCart className="w-8 h-8 text-muted-foreground/40" />
              </div>
              <p className="text-base font-medium text-muted-foreground">No hay órdenes de compra</p>
              <p className="mt-1 text-sm text-muted-foreground/70">
                {searchTerm || statusFilter !== 'all'
                  ? 'Intenta ajustar los filtros de búsqueda'
                  : 'Comienza creando una nueva orden de compra'}
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <RequirePermission permission={PERMISSIONS.COMPRAS.OC_CREAR}>
                  <Button onClick={() => navigate('/purchase-orders/new')} className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Orden de Compra
                  </Button>
                </RequirePermission>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {pageOrders.map((order) => {
                const counts = documentCounts[order.id];
                const facturas = counts?.FACTURA || 0;
                const remitos = localRemitoCounts[order.id] || 0;
                const currSymbol = order.currency === 'USD' ? 'USD' : '$';
                const canEdit = canCancelOrder(order.status);
                const canAdvance = order.status !== 'received' && order.status !== 'cancelled';
                return (
                  <div
                    key={order.id}
                    className={`flex flex-col bg-card border border-border rounded-xl overflow-hidden transition-all ${
                      order.status === 'cancelled' ? 'opacity-50' : 'hover:shadow-md hover:border-primary/20'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 pt-4 pb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="flex-shrink-0 p-2 rounded-lg bg-primary/10">
                          <ShoppingCart className="w-4 h-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-primary leading-tight">{order.orderNumber}</p>
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${order.accountingType === 'A' ? 'bg-primary/10 text-primary' : 'bg-success/10 text-success'}`}>
                              Tipo {order.accountingType}
                            </span>
                            {order.currency === 'USD' && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-warning/10 text-warning">USD</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(order.status, (canAdvance && canEditStatus) ? () => handleChangeStatus(order) : undefined)}
                    </div>

                    <div className="mx-4 border-t border-border" />

                    {/* Proveedor + fecha */}
                    <div className="px-4 py-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <DollarSign className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-foreground truncate leading-tight">{order.supplierName}</p>
                          <p className="text-xs text-muted-foreground font-mono">{order.supplierCuit}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground">
                          {new Date(order.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                    </div>

                    {/* Montos */}
                    <div className="mx-4 border-t border-border" />
                    <div className="px-4 py-3 bg-muted/30">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider mb-0.5">Subtotal</p>
                          <p className="text-xs font-medium text-foreground">{currSymbol} {order.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider mb-0.5">IVA</p>
                          <p className="text-xs font-medium text-muted-foreground">
                            {order.ivaAmount > 0 ? `${currSymbol} ${order.ivaAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider mb-0.5">Total</p>
                          <p className="text-sm font-bold text-foreground">{currSymbol} {order.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </div>
                      </div>
                    </div>

                    {/* Footer: documentos + acciones */}
                    <div className="mt-auto mx-4 border-t border-border" />
                    <div className="flex items-center justify-between px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {facturas === 0 && remitos === 0 ? (
                          <span className="text-xs text-muted-foreground">Sin docs</span>
                        ) : (
                          <>
                            {facturas > 0 && (
                              <div className="flex items-center gap-0.5" title={`${facturas} factura${facturas !== 1 ? 's' : ''}`}>
                                <FileText className="w-3.5 h-3.5 text-success" />
                                <span className="text-xs font-medium text-success">{facturas}</span>
                              </div>
                            )}
                            {remitos > 0 && (
                              <div className="flex items-center gap-0.5" title={`${remitos} remito${remitos !== 1 ? 's' : ''}`}>
                                <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                                <span className="text-xs font-medium text-muted-foreground">{remitos}</span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => handleViewDetails(order)} className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground" title="Ver detalles">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <RequirePermission permission={PERMISSIONS.COMPRAS.OC_EDITAR}>
                          <button
                            onClick={() => canEdit && navigate(`/purchase-orders/edit/${order.id}`)}
                            disabled={!canEdit}
                            className={canEdit ? 'inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground' : 'inline-flex items-center justify-center p-1.5 rounded-lg opacity-25 cursor-not-allowed text-muted-foreground'}
                            title={canEdit ? 'Editar orden' : 'No se puede editar'}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        </RequirePermission>
                        <RequirePermission permission={PERMISSIONS.COMPRAS.FACTURAS_EDITAR}>
                          <button onClick={() => handleManageDocuments(order)} className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-accent text-muted-foreground hover:text-foreground" title="Gestionar documentos">
                            <Upload className="w-3.5 h-3.5" />
                          </button>
                        </RequirePermission>
                        {order.status === 'draft' && (
                          <RequirePermission permission={PERMISSIONS.COMPRAS.OC_EDITAR}>
                            <button onClick={() => handleDeleteOrder(order)} className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors text-muted-foreground hover:text-destructive hover:bg-destructive/10" title="Eliminar orden">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </RequirePermission>
                        )}
                        {order.status === 'sent' && (
                          <RequirePermission permission={PERMISSIONS.COMPRAS.OC_EDITAR}>
                            <button onClick={() => handleCancelOrder(order)} className="inline-flex items-center justify-center p-1.5 rounded-lg transition-colors text-muted-foreground hover:text-destructive hover:bg-destructive/10" title="Cancelar orden">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </RequirePermission>
                        )}
                        <RequirePermission permission={PERMISSIONS.COMPRAS.OC_EDITAR}>
                          <button
                            onClick={() => canAdvance && handleChangeStatus(order)}
                            disabled={!canAdvance}
                            className={canAdvance ? 'inline-flex items-center justify-center p-1.5 rounded-lg transition-colors hover:bg-primary/10 text-muted-foreground hover:text-primary' : 'inline-flex items-center justify-center p-1.5 rounded-lg opacity-25 cursor-not-allowed text-muted-foreground'}
                            title={order.status === 'received' ? 'Orden completada' : order.status === 'cancelled' ? 'Cancelada' : getStatusLabel(order.status)}
                          >
                            {order.status === 'draft' && <Send className="w-3.5 h-3.5" />}
                            {order.status === 'sent' && <Check className="w-3.5 h-3.5" />}
                            {(order.status === 'received' || order.status === 'cancelled') && <Check className="w-3.5 h-3.5" />}
                          </button>
                        </RequirePermission>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {filteredOrders.length > 0 && (
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Mostrando{' '}
                <span className="font-semibold text-foreground">{pageOrders.length}</span> de{' '}
                <span className="font-semibold text-foreground">{filteredOrders.length}</span>{' '}
                orden{filteredOrders.length !== 1 ? 'es' : ''}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => { if (!open) setIsViewDialogOpen(false); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Orden de Compra</DialogTitle>
          </DialogHeader>
          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-2 h-2 rounded-full bg-primary animate-bounce"></div>
                </div>
                <p className="text-sm text-muted-foreground">Cargando detalles...</p>
              </div>
            </div>
          ) : orderToView && (
            <div className="space-y-4 py-2">
              {/* Información General */}
              <div className="p-4 border rounded-lg border-border bg-muted/40">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Información General</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">N° de Orden</div>
                    <div className="font-semibold text-primary">{orderToView.orderNumber}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Fecha</div>
                    <div className="font-medium text-foreground">{new Date(orderToView.date).toLocaleDateString('es-AR')}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Proveedor</div>
                    <div className="font-medium text-foreground">{orderToView.supplierName}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">CUIT</div>
                    <div className="font-medium text-foreground">{orderToView.supplierCuit}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Tipo Contabilidad</div>
                    <div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        orderToView.accountingType === 'A' 
                          ? 'bg-primary/10 text-primary' 
                          : 'bg-success/10 text-success'
                      }`}>
                        Tipo {orderToView.accountingType}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Moneda</div>
                    <div className="font-medium text-foreground">{orderToView.currency === 'USD' ? 'USD (Dólares)' : 'ARS (Pesos)'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">Estado</div>
                    <div>
                      {getStatusBadge(
                        orderToView.status, 
                        (orderToView.status !== 'received' && orderToView.status !== 'cancelled' && canEditStatus) 
                          ? () => {
                              setIsViewDialogOpen(false);
                              handleChangeStatus(orderToView);
                            } 
                          : undefined
                      )}
                    </div>
                  </div>
                  {orderToView.costCenter && (
                    <div className="col-span-2">
                      <div className="text-xs text-muted-foreground">Centro de Costo</div>
                      <div className="font-medium text-foreground">{orderToView.costCenter}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Líneas de Compra */}
              <div className="p-4 border rounded-lg border-border bg-muted/40">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Líneas de Compra</h3>
                <div className="space-y-3">
                  {orderToView.lines.map((line) => (
                    <div key={line.id} className="p-3 rounded-lg border border-border bg-card">
                      <div className="font-medium text-foreground">{line.description}</div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-muted-foreground md:grid-cols-4">
                        <div>
                          <span>Cantidad:</span> {line.quantity} {line.unit}
                        </div>
                        <div>
                          <span>Precio Unit:</span> {orderToView.currency === 'USD' ? 'USD' : '$'} {line.unitPrice.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                        <div className="col-span-2">
                          <span>Subtotal:</span> <span className="font-semibold text-foreground">{orderToView.currency === 'USD' ? 'USD' : '$'} {line.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>

                      {/* Distribuciones de Obras/Rubros */}
                      {line.costDistributions && line.costDistributions.length > 0 && (
                        <div className="pt-3 mt-3 border-t border-border">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-4 bg-primary rounded"></div>
                            <div className="text-xs font-semibold text-foreground">Distribución por Obras</div>
                          </div>
                          <div className="space-y-2">
                            {line.costDistributions.map((dist) => {
                              const amount = (line.subtotal * dist.percentage) / 100;
                              const quantity = (line.quantity * dist.percentage) / 100;
                              return (
                                <div key={dist.id} className="p-3 border border-primary/20 rounded-lg bg-primary/5">
                                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                    <div className="space-y-1">
                                      <div className="flex items-start gap-2">
                                        <div className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full bg-primary"></div>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs font-semibold text-foreground truncate">{dist.projectName}</div>
                                          <div className="text-xs text-muted-foreground truncate">{dist.workCategoryName}</div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                      <div className="text-center">
                                        <div className="text-primary font-semibold mb-0.5">{dist.percentage}%</div>
                                        <div className="text-muted-foreground text-[10px]">Porc.</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="font-semibold text-foreground mb-0.5">
                                          {orderToView.currency === 'USD' ? 'USD' : '$'} {amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <div className="text-muted-foreground text-[10px]">Monto</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="font-semibold text-foreground mb-0.5">{quantity.toFixed(2)}</div>
                                        <div className="text-muted-foreground text-[10px]">{line.unit}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Distribuciones de Rubros Generales */}
                      {line.generalCostDistributions && line.generalCostDistributions.length > 0 && (
                        <div className="pt-3 mt-3 border-t border-border">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1 h-4 bg-success rounded"></div>
                            <div className="text-xs font-semibold text-foreground">Distribución por Rubros Generales</div>
                          </div>
                          <div className="space-y-2">
                            {line.generalCostDistributions.map((dist) => {
                              const amount = (line.subtotal * dist.percentage) / 100;
                              const quantity = (line.quantity * dist.percentage) / 100;
                              return (
                                <div key={dist.id} className="p-3 border border-success/20 rounded-lg bg-success/5">
                                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                    <div className="space-y-1">
                                      <div className="flex items-start gap-2">
                                        <div className="flex-shrink-0 w-1.5 h-1.5 mt-1.5 rounded-full bg-success"></div>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs font-semibold text-foreground truncate">{dist.generalWorkCategoryName}</div>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 text-xs">
                                      <div className="text-center">
                                        <div className="text-success font-semibold mb-0.5">{dist.percentage}%</div>
                                        <div className="text-muted-foreground text-[10px]">Porc.</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="font-semibold text-foreground mb-0.5">
                                          {orderToView.currency === 'USD' ? 'USD' : '$'} {amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <div className="text-muted-foreground text-[10px]">Monto</div>
                                      </div>
                                      <div className="text-center">
                                        <div className="font-semibold text-foreground mb-0.5">{quantity.toFixed(2)}</div>
                                        <div className="text-muted-foreground text-[10px]">{line.unit}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Legacy: mostrar costCenter si existe */}
                      {line.costCenter && !line.costDistributions?.length && !line.generalCostDistributions?.length && (
                        <div className="pt-2 mt-2 text-xs text-muted-foreground border-t border-border">
                          <span>Centro de Costo:</span> {line.costCenter}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Totales */}
              <div className="p-4 border rounded-lg border-border bg-muted/40">
                <h3 className="mb-3 text-sm font-semibold text-foreground">Totales</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-semibold text-foreground">{orderToView.currency === 'USD' ? 'USD' : '$'} {orderToView.subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {orderToView.ivaAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">IVA ({orderToView.ivaType}):</span>
                      <span className="font-semibold text-foreground">{orderToView.currency === 'USD' ? 'USD' : '$'} {orderToView.ivaAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 text-base font-bold border-t border-border">
                    <span className="text-foreground">TOTAL ({orderToView.currency}):</span>
                    <span className="text-foreground">{orderToView.currency === 'USD' ? 'USD' : '$'} {orderToView.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>
              </div>

              {/* Documentación */}
              <div className="p-4 border rounded-lg border-border bg-muted/40">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Documentación</h3>
                  {canManageDocuments && (
                    <Button
                      size="sm"
                      onClick={() => handleManageDocuments(orderToView)}
                      className="bg-primary text-primary-foreground hover:opacity-90"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Gestionar
                    </Button>
                  )}
                </div>

                {detailInvoices.length === 0 && detailRemitos.length === 0 ? (
                  <div className="flex items-center p-3 border rounded-lg border-border bg-card">
                    <FileText className="w-5 h-5 mr-2 text-muted-foreground" />
                    <div className="text-sm text-muted-foreground">Sin documentos adjuntos</div>
                  </div>
                ) : (
                  <div className="space-y-4">

                    {/* ── Facturas ── */}
                    {detailInvoices.length > 0 && (
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Facturas ({detailInvoices.length})
                        </div>
                        <div className="space-y-2">
                          {detailInvoices.map((inv) => {
                            const hasFile = inv.fileUrl && (inv.fileUrl.startsWith('local://') || inv.fileUrl.startsWith('http'));
                            const invCurr = inv.currency === 'USD' ? 'USD' : '$';
                            return (
                              <div key={inv.id} className="flex items-center gap-3 px-3 py-2.5 border rounded-lg border-border bg-card">
                                <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-foreground truncate">{inv.invoiceNumber}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(inv.invoiceDate).toLocaleDateString('es-AR')} · {invCurr} {inv.totalAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {(() => {
                                    const statusCfg: Record<string, { label: string; cls: string }> = {
                                      PENDIENTE: { label: 'Pendiente', cls: 'bg-warning/10 text-warning' },
                                      PARCIALMENTE_PAGADA: { label: 'Parcial', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
                                      PAGADA: { label: 'Pagada', cls: 'bg-success/10 text-success' },
                                    };
                                    const { label, cls } = statusCfg[inv.paymentStatus] ?? statusCfg.PENDIENTE;
                                    return <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${cls}`}>{label}</span>;
                                  })()}
                                  {hasFile && (
                                    <button
                                      onClick={() => openDetailViewer(inv.fileUrl!, inv.invoiceNumber, 'application/pdf')}
                                      className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                                      title="Ver archivo adjunto"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* ── Remitos ── */}
                    {detailRemitos.length > 0 && (
                      <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Remitos ({detailRemitos.length})
                        </div>
                        <div className="space-y-2">
                          {detailRemitos.map((doc) => {
                            const isImg = doc.mimeType.startsWith('image/');
                            return (
                              <div key={doc.id} className="flex items-center gap-3 px-3 py-2.5 border rounded-lg border-border bg-card">
                                {isImg
                                  ? <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  : <FileText className="w-4 h-4 text-red-500 flex-shrink-0" />
                                }
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{doc.fileName}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(doc.uploadedAt).toLocaleDateString('es-AR')}
                                  </p>
                                </div>
                                <button
                                  onClick={() => openDetailRemitoViewer(doc)}
                                  className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors flex-shrink-0"
                                  title="Ver remito"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Notas */}
              {orderToView.notes && (
                <div className="p-4 border rounded-lg border-border bg-muted/40">
                  <h3 className="mb-2 text-sm font-semibold text-foreground">Notas</h3>
                  <p className="text-sm text-muted-foreground">{orderToView.notes}</p>
                </div>
              )}

              {/* Botones de Acción */}
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button
                  onClick={() => setIsViewDialogOpen(false)}
                  variant="outline"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail File Viewer Dialog */}
      {detailViewerUrl && (
        <Dialog open={!!detailViewerUrl} onOpenChange={(open) => { if (!open) closeDetailViewer(); }}>
          <DialogContent className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0">
            <DialogHeader className="flex-shrink-0 px-4 py-3 border-b border-border">
              <div className="flex items-center justify-between gap-4">
                <DialogTitle className="flex items-center gap-2 text-sm font-semibold truncate">
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  {detailViewerTitle}
                </DialogTitle>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => window.open(detailViewerUrl!, '_blank', 'noopener,noreferrer')}
                  >
                    <Eye className="w-3 h-3 mr-1.5" />
                    Abrir en pestaña
                  </Button>
                  <button
                    onClick={closeDetailViewer}
                    className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-hidden bg-muted/30">
              {detailViewerMime.startsWith('image/') ? (
                <img src={detailViewerUrl} alt={detailViewerTitle} className="w-full h-full object-contain" />
              ) : (
                <iframe src={detailViewerUrl} title={detailViewerTitle} className="w-full h-full border-0" allow="fullscreen" />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Change Status Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={(open) => { if (!open) { setIsStatusDialogOpen(false); setOrderToChangeStatus(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cambiar Estado de Orden</DialogTitle>
          </DialogHeader>
          {orderToChangeStatus && (
            <div className="space-y-4 py-2">
              {/* Información de la orden */}
              <div className="p-4 border rounded-lg border-border bg-muted/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Orden:</span>
                  <span className="font-semibold text-primary">{orderToChangeStatus.orderNumber}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Proveedor:</span>
                  <span className="text-sm font-medium text-foreground">{orderToChangeStatus.supplierName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total:</span>
                  <span className="font-semibold text-foreground">${orderToChangeStatus.total.toLocaleString('es-AR')}</span>
                </div>
              </div>

              {/* Cambio de estado */}
              <div className="p-4 border rounded-lg border-border bg-muted/40">
                <div className="flex items-center justify-center gap-4 mb-3">
                  <div className="text-center">
                    <div className="mb-1.5 text-xs font-medium text-muted-foreground uppercase">Estado Actual</div>
                    {getStatusBadge(orderToChangeStatus.status)}
                  </div>
                  <div className="text-xl text-muted-foreground">→</div>
                  <div className="text-center">
                    <div className="mb-1.5 text-xs font-medium text-muted-foreground uppercase">Nuevo Estado</div>
                    {getStatusBadge(getNextStatus(orderToChangeStatus.status) || '')}
                  </div>
                </div>
                <div className="p-3 border-l-4 border-primary rounded-r-lg bg-primary/5">
                  <p className="text-sm text-foreground">
                    {orderToChangeStatus.status === 'draft' && (
                      <>
                        <Send className="inline w-4 h-4 mr-2" />
                        Se generará un PDF con la orden de compra y se enviará al proveedor por los medios que selecciones.
                      </>
                    )}
                    {orderToChangeStatus.status === 'sent' && (
                      <>
                        <Check className="inline w-4 h-4 mr-2" />
                        Se marcará la orden como recibida. Esto indica que la mercadería ha sido recibida.
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Opciones de envío - Solo para borrador->enviado */}
              {orderToChangeStatus.status === 'draft' && (
                <div className="p-4 border rounded-lg border-border bg-muted/40">
                  <h3 className="mb-3 text-sm font-semibold text-foreground">Datos del Proveedor y Método de Envío</h3>
                  
                  {/* Datos del proveedor */}
                  <div className="p-3 mb-3 border rounded-lg border-border bg-card">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div className="flex items-start gap-2">
                        <Mail className="flex-shrink-0 w-4 h-4 mt-0.5 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">Email</div>
                          <div className="text-sm font-medium text-foreground truncate">
                            {suppliers.find(s => s.cuit === orderToChangeStatus.supplierId)?.email || 'No disponible'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <MessageCircle className="flex-shrink-0 w-4 h-4 mt-0.5 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground">WhatsApp</div>
                          <div className="text-sm font-medium text-foreground">
                            {suppliers.find(s => s.cuit === orderToChangeStatus.supplierId)?.phone || 'No disponible'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Selección de método de envío */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-foreground">Seleccionar Método de Envío *</Label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 transition-all border rounded-lg cursor-pointer hover:bg-accent"
                        style={{ borderColor: sendByEmail ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
                        <input
                          type="checkbox"
                          checked={sendByEmail}
                          onChange={(e) => setSendByEmail(e.target.checked)}
                          className="w-4 h-4 mr-3 rounded"
                        />
                        <Mail className="w-4 h-4 mr-2 text-muted-foreground" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">Enviar por Email</div>
                          <div className="text-xs text-muted-foreground">Se enviará el PDF al correo del proveedor</div>
                        </div>
                      </label>
                      <label className="flex items-center p-3 transition-all border rounded-lg cursor-pointer hover:bg-accent"
                        style={{ borderColor: sendByWhatsApp ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}>
                        <input
                          type="checkbox"
                          checked={sendByWhatsApp}
                          onChange={(e) => setSendByWhatsApp(e.target.checked)}
                          className="w-4 h-4 mr-3 rounded"
                        />
                        <MessageCircle className="w-4 h-4 mr-2 text-muted-foreground" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">Enviar por WhatsApp</div>
                          <div className="text-xs text-muted-foreground">Se enviará el PDF por WhatsApp al proveedor</div>
                        </div>
                      </label>
                    </div>
                    {!sendByEmail && !sendByWhatsApp && (
                      <div className="p-3 border rounded-lg bg-destructive/10 border-destructive/20">
                        <p className="text-xs font-medium text-destructive">
                          Debe seleccionar al menos un método de envío
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Botones de acción */}
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button variant="outline" onClick={() => { setIsStatusDialogOpen(false); setOrderToChangeStatus(null); }}>
                  Cancelar
                </Button>
                <Button onClick={confirmStatusChange} className="bg-primary text-primary-foreground hover:opacity-90">
                  Confirmar Cambio
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={(open) => { if (!open) { setIsCancelDialogOpen(false); setOrderToCancel(null); setNotifySupplierOnCancel(false); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <X className="w-5 h-5 text-destructive" />
              Cancelar Orden de Compra
            </DialogTitle>
            <DialogDescription>
              Esta acción es irreversible. Una vez cancelada, la orden no podrá ser modificada ni reactivada.
            </DialogDescription>
          </DialogHeader>
          {orderToCancel && (
            <div className="space-y-4 py-2">
              <div className="p-4 rounded-lg border border-border bg-muted/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Orden:</span>
                  <span className="font-semibold text-foreground">{orderToCancel.orderNumber}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Proveedor:</span>
                  <span className="text-sm font-medium text-foreground">{orderToCancel.supplierName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total:</span>
                  <span className="font-semibold text-foreground">
                    {orderToCancel.currency === 'USD' ? 'USD' : '$'} {orderToCancel.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className="p-3 rounded-lg border bg-destructive/10 border-destructive/20">
                <p className="text-xs font-medium text-destructive">Consecuencias:</p>
                <ul className="mt-1.5 ml-4 space-y-1 text-xs text-muted-foreground list-disc">
                  <li>La orden quedará marcada como "Cancelada"</li>
                  <li>No se podrá editar ni cambiar su estado</li>
                  <li>Los documentos asociados permanecerán disponibles</li>
                </ul>
              </div>
              {orderToCancel.status === 'sent' && (() => {
                const supplier = suppliers.find(s => s.cuit === orderToCancel.supplierCuit);
                const supplierEmail = supplier?.contactEmail || supplier?.email;
                if (!supplierEmail) return null;
                return (
                  <div className="p-3 rounded-lg border border-border bg-muted/40">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notifySupplierOnCancel}
                        onChange={(e) => setNotifySupplierOnCancel(e.target.checked)}
                        className="w-4 h-4 mt-0.5 rounded flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                          Notificar al proveedor por email
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Se enviará un email a <span className="font-medium">{supplierEmail}</span> informando que la orden fue cancelada.
                        </div>
                      </div>
                    </label>
                  </div>
                );
              })()}
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button variant="outline" onClick={() => { setIsCancelDialogOpen(false); setOrderToCancel(null); setNotifySupplierOnCancel(false); }}>
                  No, mantener orden
                </Button>
                <Button variant="destructive" onClick={confirmCancelOrder}>
                  <X className="w-4 h-4 mr-2" />
                  Sí, cancelar orden
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Order Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={(open) => { if (!open) { setIsDeleteDialogOpen(false); setOrderToDelete(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Borrar Orden de Compra
            </DialogTitle>
            <DialogDescription>
              Esta acción es irreversible. La orden será eliminada permanentemente del sistema.
            </DialogDescription>
          </DialogHeader>
          {orderToDelete && (
            <div className="space-y-4 py-2">
              <div className="p-4 rounded-lg border border-border bg-muted/40">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Orden:</span>
                  <span className="font-semibold text-foreground">{orderToDelete.orderNumber}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Proveedor:</span>
                  <span className="text-sm font-medium text-foreground">{orderToDelete.supplierName}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total:</span>
                  <span className="font-semibold text-foreground">
                    {orderToDelete.currency === 'USD' ? 'USD' : '$'} {orderToDelete.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <div className="p-3 rounded-lg border bg-destructive/10 border-destructive/20">
                <p className="text-xs font-medium text-destructive">Consecuencias:</p>
                <ul className="mt-1.5 ml-4 space-y-1 text-xs text-muted-foreground list-disc">
                  <li>La orden será eliminada permanentemente</li>
                  <li>No aparecerá en el listado de órdenes</li>
                  <li>Los documentos asociados también serán eliminados</li>
                </ul>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-border">
                <Button variant="outline" onClick={() => { setIsDeleteDialogOpen(false); setOrderToDelete(null); }}>
                  No, mantener orden
                </Button>
                <Button variant="destructive" onClick={confirmDeleteOrder}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sí, borrar orden
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Documents Management Dialog */}
      <DocumentsDialog
        open={isDocumentsDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsDocumentsDialogOpen(false);
            setOrderForDocuments(null);
          }
        }}
        order={orderForDocuments}
        suppliers={suppliers}
        onInvoiceCountChange={handleInvoiceCreated}
        onRemitoCountChange={handleRemitoCountChange}
      />
    </div>
  );
};

export default PurchaseOrders;
